import { useCallback, useEffect, useState } from 'react';

import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

export interface OneTimeEventState {
  seenAt: number | null;
  status: 'not-seen' | 'seen' | 'dismissed';
}

const PREFIX = 'one-time-event:';

const DEFAULT: OneTimeEventState = { seenAt: null, status: 'not-seen' };

export function useOneTimeEvent(key: string): {
  markDismissed: () => void;
  markSeen: () => void;
  state: OneTimeEventState;
} {
  const storageKey = `${PREFIX}${key}`;
  const [state, setState] = useState<OneTimeEventState>(() =>
    getLocalStorageItem<OneTimeEventState>(storageKey, DEFAULT)
  );

  // Re-read storage when the key changes — covers the case where the caller
  // first calls the hook with a placeholder key (e.g. `:__pending__`) before
  // the real id loads, then re-renders with the resolved key. Without this,
  // state would be stuck on the placeholder's snapshot.
  useEffect(() => {
    setState(getLocalStorageItem<OneTimeEventState>(storageKey, DEFAULT));
  }, [storageKey]);

  const markSeen = useCallback(() => {
    const next: OneTimeEventState = { seenAt: Date.now(), status: 'seen' };
    setState(next);
    setLocalStorageItem(storageKey, next);
  }, [storageKey]);

  const markDismissed = useCallback(() => {
    const next: OneTimeEventState = { seenAt: Date.now(), status: 'dismissed' };
    setState(next);
    setLocalStorageItem(storageKey, next);
  }, [storageKey]);

  return { markDismissed, markSeen, state };
}
